import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Receipt,
  Wallet,
  ClipboardList,
  MessageCircle,
  BarChart3,
  Upload,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  Zap,
  X,
  Box,
  Package as PackageIcon,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { MENU_LABEL_ID, MENU_VISIBILITY, ROLE_LABEL_ID } from '../../lib/constants';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Button } from '../ui/button';

const menuItems = [
  { key: 'dashboard', path: '/dashboard', icon: LayoutDashboard, group: 'Utama' },
  { key: 'clients', path: '/clients', icon: Users, group: 'Master' },
  { key: 'products', path: '/products', icon: Box, group: 'Master' },
  { key: 'packages', path: '/packages', icon: PackageIcon, group: 'Master' },
  { key: 'quotations', path: '/quotations', icon: FileText, group: 'Transaksi' },
  { key: 'invoices', path: '/invoices', icon: Receipt, group: 'Transaksi' },
  { key: 'payments', path: '/payments', icon: Wallet, group: 'Transaksi' },
  { key: 'receivables', path: '/receivables', icon: ClipboardList, group: 'Piutang' },
  { key: 'followups', path: '/followups', icon: MessageCircle, group: 'Piutang' },
  { key: 'reports', path: '/reports', icon: BarChart3, group: 'Laporan' },
  { key: 'importexport', path: '/import-export', icon: Upload, group: 'Tools' },
  { key: 'settings', path: '/settings', icon: SettingsIcon, group: 'Tools' },
];

const SidebarContent = ({ onNav }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const visibleItems = menuItems.filter((m) => MENU_VISIBILITY[m.key]?.includes(user?.role));
  const groups = [...new Set(visibleItems.map((m) => m.group))];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex flex-col h-full bg-background border-r border-border">
      {/* Brand */}
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Zap className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-heading font-semibold text-sm tracking-tight">Glowing AR Tracker</div>
            <div className="text-xs text-muted-foreground">Glowing Production</div>
          </div>
        </div>
      </div>

      {/* Menu */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {groups.map((group) => (
          <div key={group}>
            <div className="px-3 mb-2 text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
              {group}
            </div>
            <div className="space-y-0.5">
              {visibleItems
                .filter((m) => m.group === group)
                .map((m) => (
                  <NavLink
                    key={m.key}
                    to={m.path}
                    data-testid={`nav-${m.key}`}
                    onClick={onNav}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? 'bg-white/6 border border-white/10 text-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-white/4 border border-transparent'
                      }`
                    }
                  >
                    <m.icon className="h-4 w-4 flex-shrink-0" />
                    <span>{MENU_LABEL_ID[m.key]}</span>
                  </NavLink>
                ))}
            </div>
          </div>
        ))}
      </div>

      {/* User */}
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center text-sm font-semibold text-accent">
            {(user?.name || '?').slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name || '-'}</div>
            <div className="text-xs text-muted-foreground truncate">{ROLE_LABEL_ID[user?.role] || user?.role}</div>
          </div>
          <Button
            data-testid="logout-button"
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="h-8 w-8"
            aria-label="Keluar"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AppShell = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const currentMenu = menuItems.find((m) => location.pathname.startsWith(m.path));

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <div className="flex min-h-screen">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-72 flex-shrink-0 sticky top-0 h-screen">
          <SidebarContent />
        </aside>

        {/* Main Area */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Topbar */}
          <header className="sticky top-0 z-30 backdrop-blur bg-background/70 border-b border-border">
            <div className="flex items-center gap-3 px-4 md:px-6 lg:px-8 py-3">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden"
                    data-testid="mobile-menu-button"
                    aria-label="Menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-72 bg-background border-r border-border">
                  <SidebarContent onNav={() => setMobileOpen(false)} />
                </SheetContent>
              </Sheet>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground">Glowing Production</div>
                <div className="font-heading font-semibold text-base md:text-lg truncate">
                  {currentMenu ? MENU_LABEL_ID[currentMenu.key] : 'Glowing AR Tracker'}
                </div>
              </div>
            </div>
          </header>

          <main className="flex-1 px-4 md:px-6 lg:px-8 py-4 md:py-6">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppShell;
